import { useEffect, useMemo, useState } from 'react'
import type { SlideTransition } from './content-pages'

export type OverlayPosition =
  | 'TICKER_LEFT'
  | 'TICKER_SIDE'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT'

export type OverlayCardType = 'TEXT' | 'CLOCK' | 'DATE'

export interface OverlayCard {
  id: string
  type?: OverlayCardType
  line1: string
  line2?: string | null
  icon?: string | null
  durationMs?: number
  timezone?: string
  locale?: string
  showSeconds?: boolean
  hourCycle?: 'H12' | 'H24'
}

export interface Overlay {
  id: string
  name: string
  position: OverlayPosition
  cards: OverlayCard[]
  defaultDurationMs: number
  transition: SlideTransition
  backgroundColor: string
  textColor: string
  fontFamily: string
  fontSize: number
  fontWeight: number
  borderRadius: number
  createdAt: string
  updatedAt: string
}

function transitionClass(transition: SlideTransition) {
  switch (transition) {
    case 'FADE':
      return 'dotit-animate-fade'
    case 'SLIDE_LEFT':
      return 'dotit-animate-slide-left'
    case 'SLIDE_RIGHT':
      return 'dotit-animate-slide-right'
    case 'ZOOM':
      return 'dotit-animate-zoom'
    case 'WIPE':
      return 'dotit-animate-wipe'
    default:
      return ''
  }
}

function dynamicCardLines(card: OverlayCard, now: Date) {
  const type = card.type ?? 'TEXT'
  if (type === 'TEXT') {
    return { line1: card.line1, line2: card.line2 }
  }
  const locale = card.locale || 'en-GB'
  const timeZone = card.timezone || 'Asia/Dhaka'
  try {
    const line1 =
      type === 'CLOCK'
        ? new Intl.DateTimeFormat(locale, {
            timeZone,
            hour: '2-digit',
            minute: '2-digit',
            second: card.showSeconds === false ? undefined : '2-digit',
            hour12: card.hourCycle !== 'H24',
          }).format(now)
        : new Intl.DateTimeFormat(locale, {
            timeZone,
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          }).format(now)
    return { line1, line2: card.line1 || card.line2 }
  } catch {
    return {
      line1: type === 'CLOCK' ? '--:--' : 'Date unavailable',
      line2: card.line1,
    }
  }
}

export function OverlayCardPreview({
  overlay,
  compact = false,
}: {
  overlay: Overlay
  compact?: boolean
}) {
  const [index, setIndex] = useState(0)
  const [now, setNow] = useState(() => new Date())
  const card = overlay.cards[index % Math.max(overlay.cards.length, 1)]
  const lines = useMemo(
    () => (card ? dynamicCardLines(card, now) : null),
    [card, now]
  )
  useEffect(() => {
    setIndex(0)
  }, [overlay.id])
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  useEffect(() => {
    if (!card || overlay.cards.length < 2) return
    const timer = window.setTimeout(
      () => setIndex((current) => (current + 1) % overlay.cards.length),
      Math.max(1000, card.durationMs ?? overlay.defaultDurationMs)
    )
    return () => window.clearTimeout(timer)
  }, [card, overlay.cards.length, overlay.defaultDurationMs])
  if (!card || !lines) return null
  return (
    <div
      key={`${card.id}-${index}`}
      className={`flex items-center gap-2 overflow-hidden ${transitionClass(overlay.transition)} ${compact ? 'h-full px-3' : 'min-w-36 px-4 py-3 shadow-lg'}`}
      style={{
        backgroundColor: overlay.backgroundColor,
        color: overlay.textColor,
        borderRadius: compact ? 0 : overlay.borderRadius,
        fontFamily: overlay.fontFamily,
        fontSize: compact
          ? '0.8em'
          : `clamp(12px, ${overlay.fontSize / 18}vw, ${overlay.fontSize}px)`,
        fontWeight: overlay.fontWeight,
      }}
    >
      {card.icon ? (
        <span className='shrink-0 text-[1.2em]' aria-hidden='true'>
          {card.icon}
        </span>
      ) : null}
      <span className='min-w-0 leading-tight'>
        <span className='block truncate tabular-nums'>{lines.line1}</span>
        {lines.line2 ? (
          <span className='block truncate text-[0.72em] opacity-80'>
            {lines.line2}
          </span>
        ) : null}
      </span>
    </div>
  )
}

export function OverlayLayer({ overlay }: { overlay?: Overlay | null }) {
  if (
    !overlay ||
    overlay.position === 'TICKER_LEFT' ||
    overlay.position === 'TICKER_SIDE'
  )
    return null
  const position = {
    TOP_LEFT: 'top-4 left-4',
    TOP_RIGHT: 'top-4 right-4',
    BOTTOM_LEFT: 'bottom-4 left-4',
    BOTTOM_RIGHT: 'right-4 bottom-4',
  }[overlay.position]
  return (
    <div
      className={`pointer-events-none absolute z-[110] max-w-[42%] ${position}`}
    >
      <OverlayCardPreview overlay={overlay} />
    </div>
  )
}
